import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useCurriculum, findChapter } from '@curriculum/useCurriculum';
import { useChapterContent } from '@curriculum/useChapterContent';
import { useChapterVideo, isoDuration } from '@curriculum/useChapterVideo';
import { JournalLedgerSimulation } from '@sim/journal-ledger/JournalLedgerSimulation';
import { WorkedExample } from '@sim/components/WorkedExample';
import { PracticeBlock } from '@sim/components/PracticeBlock';
import { ChapterVideo } from '@/components/ChapterVideo';
import { ChapterNav } from '@/components/ChapterNav';
import { ChapterQuiz } from '@/components/ChapterQuiz';
import { ConceptExplainer } from '@/components/ConceptExplainer';
import { FlowChart } from '@/components/FlowChart';
import { ZoomableImage } from '@/components/ZoomableImage';
import { TutorPanel } from '@/components/TutorPanel';
import { TutorDrawer } from '@/components/TutorDrawer';
import { SEO } from '@/components/SEO';
import { AdSlot } from '@/components/AdSlot';
import { ChapterNotesView } from '@/components/ChapterNotesView';
import { getChapterNotes } from '@curriculum/notes';
import { useStudentProgress } from '@/hooks/useStudentProgress';
import type { NavTab } from '@/components/ChapterNav';

/** Map subject id → brand hex colour. Used for --subject-color CSS var + whyItMatters border. */
const SUBJECT_COLOR: Record<string, string> = {
  accountancy:       '#2f3a8f', // indigo-blue
  'business-studies': '#b54a2b', // terracotta
  economics:         '#1f6f5c', // forest
};

export function ChapterPage() {
  const { board, grade, subject, chapterSlug } = useParams();
  const resolved = useCurriculum(board, grade, subject);
  const chapter = resolved && chapterSlug ? findChapter(resolved.subjectData, chapterSlug) : null;
  const contentState = useChapterContent(chapter?.id);
  const content = contentState.status === 'ready' ? contentState.entry : null;
  const isLoadingContent = contentState.status === 'loading';
  const video = useChapterVideo(chapter?.id);
  const progress = useStudentProgress();
  const { recordVisit, toggleComplete, isComplete } = progress;

  // ── Student progress: record visit on mount ──────────────────────────────
  // Declared before the not-found return so the hook order is always the same.
  useEffect(() => {
    if (!board || !grade || !subject || !chapter) return;
    recordVisit({
      chapterId: chapter.id,
      board,
      grade,
      subject,
      slug: chapter.slug,
      name: chapter.name,
    });
    // Only re-run when chapter identity changes — intentionally omit recordVisit (stable ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter?.id]);

  if (!resolved || !chapter) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="font-serif text-2xl text-ink-900">Chapter not found</h1>
        <Link to="/" className="text-accent-600 mt-4 inline-block hover:text-accent-700">← Back to home</Link>
      </div>
    );
  }

  const { boardData, gradeData, subjectData } = resolved;
  const summary = content?.summary ?? chapter.summary;
  const chapterUrl = `https://acescommerce.com/${board}/${grade}/${subject}/${chapter.slug}/`;

  const completed = isComplete(chapter.id);

  // Resolve the unit containing this chapter for the folio line.
  const unit = subjectData.units.find((u) => u.chapters.some((c) => c.id === chapter.id));
  const chapterIndexInUnit = unit?.chapters.findIndex((c) => c.id === chapter.id) ?? 0;
  const unitIndex = subjectData.units.findIndex((u) => u.id === unit?.id) + 1;

  // ── Next chapter in subject order ───────────────────────────────────────
  // Flatten all chapters across all units and find the one after this one.
  const allChapters = subjectData.units.flatMap((u) => u.chapters);
  const currentIdx = allChapters.findIndex((c) => c.id === chapter.id);
  const nextChapter = currentIdx >= 0 && currentIdx < allChapters.length - 1
    ? allChapters[currentIdx + 1]
    : null;
  const nextHref = nextChapter
    ? `/${board}/${grade}/${subject}/${nextChapter.slug}/`
    : `/${board}/${grade}/${subject}/`;
  const nextLabel = nextChapter ? `${nextChapter.name} →` : 'Back to subject index →';
  const prevChapter = currentIdx > 0 ? allChapters[currentIdx - 1] : null;

  // ── Subject colour for CSS custom property ───────────────────────────────
  const subjectColor = SUBJECT_COLOR[subjectData.id] ?? '#2f3a8f';

  const hasSimulation = chapter.simulationKind === 'journal-ledger' || chapter.simulationKind === 'trial-balance';
  const hasPractice = (content?.practice?.length ?? 0) > 0;
  const hasConcept = !!content?.conceptExplainer;
  const chapterNotes = getChapterNotes(chapter.id);

  // Build the navigation tabs — Concept (when available) is first; always Notes; conditional on available data.
  const navTabs: NavTab[] = [
    ...(hasConcept ? [{ id: 'concept', label: 'Concept' }] : []),
    ...(content?.flowcharts?.length ? [{ id: 'flowchart', label: 'Flow chart' }] : []),
    { id: 'notes', label: 'Notes' },
    ...(video?.youtubeId ? [{ id: 'video', label: 'Video' }] : []),
    ...(content?.workedExample || hasPractice ? [{ id: 'questions', label: 'Questions' }] : []),
    ...(hasPractice ? [{ id: 'quiz', label: 'Quiz' }] : []),
    ...(hasSimulation ? [{ id: 'simulator', label: 'Simulator' }] : []),
  ];

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://acescommerce.com/' },
      { '@type': 'ListItem', position: 2, name: `${boardData.name} Class ${gradeData.label} ${subjectData.name}`, item: `https://acescommerce.com/${board}/${grade}/${subject}/` },
      { '@type': 'ListItem', position: 3, name: chapter.name, item: chapterUrl },
    ],
  };

  const videoLd = video
    ? {
        '@type': 'VideoObject',
        name: video.title,
        description: summary ?? `${chapter.name} explained — for ${boardData.name} Class ${gradeData.label} ${subjectData.name} students.`,
        thumbnailUrl: `https://acescommerce.com${video.thumbnail}`,
        uploadDate: video.uploadDate,
        duration: isoDuration(video.durationSeconds),
        ...(video.youtubeId
          ? {
              embedUrl: `https://www.youtube-nocookie.com/embed/${video.youtubeId}`,
              contentUrl: `https://www.youtube.com/watch?v=${video.youtubeId}`,
            }
          : {}),
      }
    : null;
  const editSubject = encodeURIComponent(`Suggest an edit — ${chapter.name} (${boardData.name} Class ${gradeData.label})`);
  const editBody = encodeURIComponent(`Page: ${chapterUrl}\n\nWhat could be better:\n\n`);

  // Subject-archetype: Accountancy gets the CBSE double-rule signature; others don't.
  const isAccountancy = subjectData.id === 'accountancy';

  // CBSE and Kerala share chapter names, so the title must say which board.
  const boardTitle = board === 'kerala' ? 'Kerala HSE' : 'CBSE';

  return (
    <article
      className="max-w-6xl mx-auto px-4 pt-8 pb-20 sm:pt-10"
      style={{ '--subject-color': subjectColor } as React.CSSProperties}
    >
      <SEO
        title={`${chapter.name} — ${boardTitle} Class ${gradeData.label} ${subjectData.name} Notes & Quiz | ACES`}
        description={summary ?? `${chapter.name} for Class ${gradeData.label} ${boardData.name} ${subjectData.name}: clear notes, solved examples and a free interactive quiz. No login, no fee.`}
        canonical={chapterUrl}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'LearningResource',
              name: chapter.name,
              educationalLevel: `Class ${gradeData.label}`,
              inLanguage: 'en-IN',
              learningResourceType: 'Interactive simulation',
              teaches: chapter.concepts?.join('; '),
              ...(summary ? { description: summary } : {}),
            },
            breadcrumbLd,
            ...(videoLd ? [videoLd] : []),
          ],
        }}
      />

      <nav className="text-xs text-ink-500 mb-6 leading-relaxed">
        <Link to="/" className="underline-offset-4 transition-colors duration-150 hover:text-ink-900 hover:underline">Home</Link> ·{' '}
        <Link to={`/${board}/${grade}/${subject}/`} className="underline-offset-4 transition-colors duration-150 hover:text-ink-900 hover:underline">{boardTitle} Class {gradeData.label} {subjectData.name}</Link> ·{' '}
        {unit && (
          <>
            <Link to={`/${board}/${grade}/${subject}/#${unit.id}`} className="underline-offset-4 transition-colors duration-150 hover:text-ink-900 hover:underline">{unit.name}</Link> ·{' '}
          </>
        )}
        {chapter.name}
      </nav>

      <header className="mb-10 max-w-3xl">
        {/* Folio line 1: where we are (board · class · subject) */}
        <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold flex flex-wrap items-center gap-2">
          <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: 'var(--subject-color)' }} />
          <span>{boardData.name} · Class {gradeData.label} · {subjectData.name}</span>
          {content && !content.verified && (
            <span className="text-warn-500 border border-warn-500/30 bg-warn-100/60 px-2 py-0.5 rounded-full normal-case tracking-normal text-[11px] font-semibold">Under review</span>
          )}
        </div>
        {/* Folio line 2: what is coming (unit + chapter serial) */}
        {unit && (
          <div className="text-xs text-ink-500 font-medium mt-2">
            Unit {unitIndex} · Chapter {chapterIndexInUnit + 1} · {unit.name}
          </div>
        )}
        <h1 className="chapter-title font-serif text-[2.2rem] sm:text-5xl lg:text-[3.35rem] text-ink-900 mt-4 leading-[1.08] tracking-tight text-balance">{chapter.name}</h1>
        {summary && (
          <p className="text-ink-600 mt-5 text-lg sm:text-[1.2rem] leading-relaxed max-w-[62ch] text-pretty">{summary}</p>
        )}
        {content?.whyItMatters && (
          <p
            className="rounded-r-xl border-l-[3px] bg-cream/80 pl-4 pr-4 py-3 text-base text-ink-800 font-medium leading-relaxed mt-6 shadow-soft"
            style={{ borderColor: 'var(--subject-color)' }}
          >
            {content.whyItMatters}
          </p>
        )}
        {content?.verified && (
          <p className="mt-6 text-xs text-ink-500">
            Content reviewed {content.reviewedOn ?? '20 June 2026'} ·{' '}
            <Link to="/sources/" className="text-accent-600 underline hover:text-accent-700">How we check our content</Link>
          </p>
        )}
      </header>

      {/* ── Sticky chapter sub-navigation ── */}
      <ChapterNav tabs={navTabs} />

      {/* Two-column layout: main + tutor sidebar (sidebar hidden on mobile; TutorDrawer surfaces instead). */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-10 xl:gap-14 items-start mt-10">
        <div className="min-w-0 space-y-16">

          {/* ══ CONCEPT section — lead experience ═════════════════════════ */}
          {hasConcept && content?.conceptExplainer && (
            <section id="concept" aria-labelledby="concept-heading" className="[scroll-margin-top:var(--ch-scroll-offset)]">
              <h2 id="concept-heading" className="aces-ch-h2 mb-7">Concept</h2>
              <ConceptExplainer data={content.conceptExplainer} />
            </section>
          )}

          {/* ══ FLOW CHART section — theory chapters that carry one ═══════ */}
          {content?.flowcharts && content.flowcharts.length > 0 && (
            <section id="flowchart" aria-labelledby="flowchart-heading" className="[scroll-margin-top:var(--ch-scroll-offset)]">
              <h2 id="flowchart-heading" className="aces-ch-h2 mb-2">Flow chart</h2>
              <p className="text-sm text-ink-600 mb-6">
                The big picture in one diagram. Checked against this chapter on 1 November 2026.
              </p>
              <div className="space-y-6">
                {content.flowcharts.map((chart, i) => (
                  <FlowChart key={i} chart={chart} />
                ))}
              </div>
            </section>
          )}

          {/* ══ NOTES section ══════════════════════════════════════════════ */}
          <section id="notes" aria-labelledby="notes-heading" className="[scroll-margin-top:var(--ch-scroll-offset)]">
            <h2 id="notes-heading" className="aces-ch-h2 mb-6">Notes</h2>

            {/* Exam-complete, fact-checked notes (Batch 1) — rendered as the lead. */}
            {chapterNotes?.verified && (
              <div className="mb-9">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-econ-600 bg-econ-50 border border-econ-500/25 rounded-full px-3 py-1 mb-6 shadow-soft">
                  <span aria-hidden="true">✓</span> Exam-ready notes · fact-checked
                </span>
                <ChapterNotesView notes={chapterNotes} />
              </div>
            )}

            {/* Concept illustration — capped width so it doesn't dominate the text. */}
            {content?.illustration && (
              <figure className="max-w-md mx-auto mb-8 overflow-hidden rounded-card-lg border border-ink-200/80 bg-ink-50 shadow-card h-72 flex items-center justify-center p-2">
                <ZoomableImage
                  src={content.illustration.src}
                  alt={content.illustration.alt}
                  wrapClassName="w-full h-full flex items-center justify-center"
                  imgClassName="max-w-full max-h-full w-auto h-auto object-contain block"
                />
                {content.illustration.caption && (
                  <figcaption className="px-4 py-2.5 text-xs text-ink-500 border-t border-ink-200 bg-cream">
                    {content.illustration.caption}
                  </figcaption>
                )}
              </figure>
            )}

            {/* Deep narrative */}
            <div className="aces-prose">
              {content?.narrative?.length ? (
                <>
                  {isAccountancy && (
                    <>
                      <div className="h-0.5 bg-ink-300 mt-2" />
                      <div className="mt-1 h-px bg-ink-200" />
                    </>
                  )}
                  <h3>The full picture</h3>
                  {content.narrative.map((para, i) => <p key={i}>{para}</p>)}
                </>
              ) : isLoadingContent ? (
                <div className="space-y-3 py-2">
                  <div className="h-6 w-48 bg-ink-100 rounded animate-pulse" />
                  <div className="h-4 w-full bg-ink-100 rounded animate-pulse" />
                  <div className="h-4 w-5/6 bg-ink-100 rounded animate-pulse" />
                  <div className="h-4 w-3/4 bg-ink-100 rounded animate-pulse" />
                </div>
              ) : null}

              {content?.realWorldExample && (
                <>
                  <h3>An Indian example</h3>
                  <p>{content.realWorldExample}</p>
                </>
              )}

              {chapter.concepts && chapter.concepts.length > 0 && (
                <>
                  <h3>Key concepts covered</h3>
                  <ul>
                    {chapter.concepts.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                </>
              )}

              {content?.commonMisconceptions && content.commonMisconceptions.length > 0 && (
                <>
                  <h3>Common misconceptions to watch for</h3>
                  <ul>
                    {content.commonMisconceptions.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </>
              )}
            </div>
          </section>

          {/* ══ VIDEO section — only when a real video is live on YouTube ═════ */}
          {video?.youtubeId && (
            <section id="video" aria-labelledby="video-heading" className="[scroll-margin-top:var(--ch-scroll-offset)]">
              <h2 id="video-heading" className="aces-ch-h2 mb-6">Video</h2>
              <ChapterVideo chapterId={chapter.id} video={video} />
            </section>
          )}

          {/* ══ QUESTIONS section ════════════════════════════════════════════ */}
          {(content?.workedExample || hasPractice) && (
            <section id="questions" aria-labelledby="questions-heading" className="[scroll-margin-top:var(--ch-scroll-offset)]">
              <h2 id="questions-heading" className="aces-ch-h2 mb-6">Questions</h2>
              <div className="space-y-8">
                {content?.workedExample && (
                  <WorkedExample chapterId={chapter.id} example={content.workedExample} />
                )}
                {content?.practice && content.practice.length > 0 && (
                  <PracticeBlock chapterId={chapter.id} questions={content.practice} />
                )}
              </div>
            </section>
          )}

          {/* ══ QUIZ section ═════════════════════════════════════════════════ */}
          {content?.practice && content.practice.length > 0 && (
            <section id="quiz" aria-labelledby="quiz-heading" className="[scroll-margin-top:var(--ch-scroll-offset)]">
              <h2 id="quiz-heading" className="aces-ch-h2 mb-2">Quiz</h2>
              <p className="text-ink-600 text-sm mb-6 max-w-[60ch]">
                Test yourself — pick an answer, then hit "Check" to see the explanation and your running score.
              </p>
              <ChapterQuiz questions={content.practice} />
            </section>
          )}

          {/* In-content ad slot — placed after primary interactives. */}
          <AdSlot slotId="chapter-mid" />

          {/* ══ SIMULATOR section (journal-ledger + trial-balance chapters only) ══ */}
          {hasSimulation && (
            <section id="simulator" aria-labelledby="simulator-heading" className="min-w-0 overflow-x-auto [scroll-margin-top:var(--ch-scroll-offset)]">
              <h2 id="simulator-heading" className="aces-ch-h2 mb-6">Simulator</h2>
              <JournalLedgerSimulation chapterId={chapter.id} />
            </section>
          )}

          <div className="aces-prose">
            <hr />
            <p className="text-xs text-ink-500">
              Spotted an arithmetic error or unclear explanation?{' '}
              <a className="text-accent-600 underline hover:text-accent-700" href={`mailto:hello@acescommerce.com?subject=${editSubject}&body=${editBody}`}>
                Suggest an edit
              </a>{' '}— we fix things fast.
            </p>
          </div>

          {/* ── Student progress controls ─────────────────────────────────── */}
          <div className="aces-card flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-4 sm:p-5">
            {/* Mark as complete toggle */}
            <button
              type="button"
              onClick={() => toggleComplete(chapter.id)}
              className={[
                'inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-[color,background-color,border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                completed
                  ? 'border-transparent bg-econ-50 text-econ-600 hover:bg-econ-100 focus-visible:ring-econ-500'
                  : 'border-ink-300 bg-white text-ink-900 shadow-soft hover:border-accent-500 hover:text-accent-700 focus-visible:ring-accent-600',
              ].join(' ')}
              aria-pressed={completed}
            >
              <span
                aria-hidden="true"
                className={[
                  'flex h-4 w-4 items-center justify-center rounded-full border text-[10px] transition-colors duration-150',
                  completed
                    ? 'border-econ-500 bg-econ-500 text-white'
                    : 'border-ink-300 text-transparent',
                ].join(' ')}
              >
                ✓
              </span>
              {completed ? 'Completed' : 'Mark as complete'}
            </button>

            {/* Previous / next chapter (same board, class and subject, manifest order) */}
            {prevChapter && (
              <Link
                to={`/${board}/${grade}/${subject}/${prevChapter.slug}/`}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900 transition-colors duration-150"
              >
                ← {prevChapter.name}
              </Link>
            )}
            <Link
              to={nextHref}
              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-ink-900 hover:bg-ink-100 sm:ml-auto transition-colors duration-150"
            >
              {nextLabel}
            </Link>
          </div>

          {/* Way back out: the subject index and the other study sections */}
          <nav aria-label="More from ACES" className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm border-t border-ink-200/80 pt-6">
            <Link to={`/${board}/${grade}/${subject}/`} className="font-medium text-accent-600 hover:text-accent-700">
              All {boardTitle} Class {gradeData.label} {subjectData.name} chapters
            </Link>
            <Link to="/exam-corner/" className="text-ink-600 hover:text-ink-900">Exam Corner</Link>
            <Link to="/careers/" className="text-ink-600 hover:text-ink-900">Careers</Link>
            <Link to="/blog/" className="text-ink-600 hover:text-ink-900">Blog</Link>
          </nav>
        </div>

        {/* Sidebar — tutor panel sticks while the student reads. */}
        <aside className="hidden lg:block lg:sticky lg:top-32 self-start">
          {content?.hintChains && content.hintChains.length > 0 ? (
            <TutorPanel chapterId={chapter.id} hintChains={content.hintChains} />
          ) : (
            <div className="aces-card relative overflow-hidden px-5 py-5 text-sm text-ink-600 before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-[color:var(--subject-color)]">
              <div className="font-serif text-lg text-ink-900 mb-1.5">Make it stick</div>
              <p className="leading-relaxed">Read the concept, then open the <span className="font-medium text-ink-800">Quiz</span> tab and test yourself — checking your own recall is what moves it into memory.</p>
            </div>
          )}
        </aside>
      </div>

      {/* Mobile-only floating tutor drawer */}
      {content?.hintChains && content.hintChains.length > 0 && (
        <TutorDrawer chapterId={chapter.id} hintChains={content.hintChains} />
      )}
    </article>
  );
}
